import { coerceCurrency, Currency } from '../interfaces';

/**
 * Lucide icon names for currencies the pack actually ships.
 * Names are the icon id (`euro`), used in the app as `@tui.euro`.
 * Currencies omitted here fall back to {@link DefaultCurrencyLucideIcon}.
 */
export const CurrencyLucideIcons: Partial<Record<Currency, string>> = {
	USD: 'dollar-sign',
	EUR: 'euro',
	JPY: 'japanese-yen',
	GBP: 'pound-sterling',
	CHF: 'swiss-franc',
};

export const DefaultCurrencyLucideIcon = 'dollar-sign';

/**
 * Lucide icon for an analysis currency. Unknown codes and currencies
 * without a Lucide icon use the dollar sign.
 */
export function currencyLucideIcon(currency: unknown): string {
	return (
		CurrencyLucideIcons[coerceCurrency(currency)] ??
		DefaultCurrencyLucideIcon
	);
}

/**
 * Short text symbols for labels such as `Revenue {{symbol}}` and `CM{{symbol}}`.
 * Keep these aligned with the symbols used for formatted amounts.
 * Unknown codes fall back to {@link DefaultCurrencyTextSymbol}.
 */
export const CurrencyTextSymbols: Record<Currency, string> = {
	USD: '$',
	EUR: '€',
	JPY: '¥',
	GBP: '£',
	AUD: 'A$',
	CAD: 'CA$',
	CHF: 'CHF',
	CNH: 'CNH',
	HKD: 'HK$',
	NZD: 'NZ$',
};

export const DefaultCurrencyTextSymbol = '$';

/**
 * Text symbol for an analysis currency. Unknown codes use `$`.
 */
export function currencyTextSymbol(currency: unknown): string {
	return (
		CurrencyTextSymbols[coerceCurrency(currency)] ??
		DefaultCurrencyTextSymbol
	);
}

type FormatCurrencyAmountOptions = {
	round?: boolean;
	locale?: string;
};

const NarrowSpace = /\u202F|\u00A0/g;

/**
 * Symbol used for a currency in the active locale (`$`, `€`, `A$`, `CHF`).
 * Alphabetic symbols are not given a trailing space; callers that prefix an
 * input can use {@link currencyPrefix}.
 */
export function currencySymbol(currency: Currency, locale = 'en'): string {
	try {
		const part = new Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
			currencyDisplay: 'symbol',
		})
			.formatToParts(0)
			.find((entry) => entry.type === 'currency');
		return part?.value || currency;
	} catch {
		return currency;
	}
}

/**
 * Prefix for inputs and compact labels. Letter-only symbols (`CHF`, `CNH`)
 * keep a space so they do not sit against the digits.
 */
export function currencyPrefix(currency: Currency, locale = 'en'): string {
	const symbol = currencySymbol(currency, locale);
	return /^[A-Za-z]+$/.test(symbol) ? `${symbol} ` : symbol;
}

/**
 * Formats a major-unit amount (dollars, euros, …) without converting it.
 * Fraction digits stay at 2 unless `round` is set, including for yen, so the
 * stored cent value stays visible.
 */
export function formatCurrencyAmount(
	amount: number,
	currency: Currency,
	options?: FormatCurrencyAmountOptions,
): string {
	const locale = options?.locale ?? 'en';
	const digits = options?.round ? 0 : 2;
	const safeAmount = Number.isFinite(amount) ? amount : 0;

	try {
		return new Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
			currencyDisplay: 'symbol',
			minimumFractionDigits: digits,
			maximumFractionDigits: digits,
		})
			.format(safeAmount)
			.replace(NarrowSpace, ' ');
	} catch {
		const formatted = safeAmount.toLocaleString(locale, {
			minimumFractionDigits: digits,
			maximumFractionDigits: digits,
		});
		return `${currencyPrefix(coerceCurrency(currency), locale)}${formatted}`;
	}
}
